import useScopedLoad from './hooks/useScopedLoad';
import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import ConfirmationDialog from './components/ConfirmationDialog';
import ScenarioStatus from './components/ScenarioStatus';
import QuantityWithUnit from './components/QuantityWithUnit';
import { listBarrelAllocations, listBarrelDispatches, deliverDispatch, startDispatch } from './api/tumblerApi';
import DispatchPlanForm from './DispatchPlanForm';
import normalizeApiError from './utils/normalizeApiError';
import { formatBangkokDateTime } from './utils/bangkokTime';

// IMPLEMENTATION_PLAN.md Phase 4 — barrel dispatch to downstream lines.
// Start -> IN_TRANSIT -> Deliver -> DELIVERED. A mutation success refetches the list (and the
// caller should refresh scenario/stock views too) instead of the UI computing the stock delta
// itself ("มuutation สำเร็จแล้ว refresh stock/allocation/scenario ไม่คำนวณยอดหักเองใน UI").
// Cannot deliver before start is enforced by only showing Deliver once status is IN_TRANSIT.
export default function DispatchBoard({ plant, factory }) {
  const accessToken = useSelector((state) => state.auth.access_token);

  const [busyId, setBusyId] = useState(null);
  const [message, setMessage] = useState(null);
  const [deliverTarget, setDeliverTarget] = useState(null);


  const loader = useCallback(async (signal) => {
    const [dispatches, allocations] = await Promise.all([
      listBarrelDispatches({ plant, factory, accessToken, signal }),
      listBarrelAllocations({ plant, factory, accessToken, signal }),
    ]);
    return { dispatches: dispatches.data, allocations: allocations.data };
  }, [plant, factory, accessToken]);
  const read = useScopedLoad(`${plant}/${factory}`, loader, Boolean(accessToken && plant && factory));
  const state = { ...read, results: read.data?.dispatches || [] };
  const pending = (read.data?.allocations || []).filter((row) => row.scenario_status === 'PUBLISHED'
    && (!row.dispatch_id || ['PLANNED', 'RELEASED'].includes(row.dispatch_status)))
    .sort((a, b) => String(a.sku).localeCompare(String(b.sku))
      || String(a.expiry_at || '9999').localeCompare(String(b.expiry_at || '9999')));
  const load = read.reload;


  const runStart = async (dispatch) => {
    setBusyId(dispatch.id);
    setMessage(null);
    try {
      await startDispatch({ dispatchId: dispatch.id, plant, factory, accessToken });
      setMessage({ severity: 'success', text: `Dispatch #${dispatch.id} started.` });
      await load();
    } catch (error) {
      setMessage({ severity: 'error', text: normalizeApiError(error, 'Unable to start dispatch.').summary });
    } finally {
      setBusyId(null);
    }
  };

  const confirmDeliver = async () => {
    if (!deliverTarget) return;
    setBusyId(deliverTarget.id);
    setMessage(null);
    try {
      await deliverDispatch({ dispatchId: deliverTarget.id, plant, factory, accessToken });
      setMessage({ severity: 'success', text: `Dispatch #${deliverTarget.id} delivered. Stock and allocation changes confirmed by server.` });
      await load();
    } catch (error) {
      setMessage({ severity: 'error', text: normalizeApiError(error, 'Unable to record delivery.').summary });
    } finally {
      setBusyId(null);
      setDeliverTarget(null);

    }
  };

  if (state.loading && !state.results.length) {
    return <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 160 }}><CircularProgress size={28} /></Box>;
  }

  if (state.error) {
    return <Alert severity="error">{state.error.summary}</Alert>;
  }

  return (
    <Box>
      {message && <Alert severity={message.severity} onClose={() => setMessage(null)} sx={{ mb: 2 }}>{message.text}</Alert>}
      <Typography sx={{ mb: 2 }}>Dispatch moves a whole physical barrel (up to 200 kg). Transport and estimated line consumption are shown separately.</Typography>
      {pending.length > 0 && <Stack spacing={2} sx={{ mb: 3 }}>
        <Typography variant="h6">Plan / adjust reserved barrels</Typography>
        {pending.map((allocation) => <DispatchPlanForm key={`${allocation.id}/${allocation.dispatch_id || ''}`}
          allocation={allocation} plant={plant} factory={factory} accessToken={accessToken} onSaved={load} />)}
      </Stack>}
      {!state.results.length ? (
        <Typography color="text.secondary">No barrel dispatches for this plant/factory.</Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Box component="table" sx={{ width: '100%', minWidth: 960, borderCollapse: 'collapse', '& th, & td': { p: 0.75, borderBottom: '1px solid', borderColor: 'divider' }, '& th': { textAlign: 'left', fontWeight: 600 } }}>
            <thead>
              <tr>{['ID', 'Barrel', 'Line / PO', 'Physical qty', 'Transport departure / arrival', 'Estimated consumption', 'Line kg/hr', 'Actual departure / arrival', 'Ready / expiry', 'Arrival lateness', 'Status', ''].map((label) => <th key={label}>{label}</th>)}</tr>
            </thead>
            <tbody>
              {state.results.map((dispatch) => (
                <tr key={dispatch.id}>
                  <td>#{dispatch.id}</td>
                  <td>{dispatch.core_tumbler}</td>
                  <td>{dispatch.downstream_line} / {dispatch.downstream_process_order || '—'}</td>
                  <td><QuantityWithUnit value={dispatch.quantity_kg} unit="kg" /></td>
                  <td>{formatBangkokDateTime(dispatch.planned_start_at)} → {formatBangkokDateTime(dispatch.planned_end_at)}</td>
                  <td>{formatBangkokDateTime(dispatch.planned_consumption_start_at)} → {formatBangkokDateTime(dispatch.planned_consumption_end_at)}</td>
                  <td>{dispatch.capacity_kg_hr}</td>
                  <td>{dispatch.actual_start_at ? formatBangkokDateTime(dispatch.actual_start_at) : '—'} → {dispatch.actual_end_at ? formatBangkokDateTime(dispatch.actual_end_at) : '—'}</td>
                  <td>{dispatch.barrel_ready_at ? formatBangkokDateTime(dispatch.barrel_ready_at) : '—'} / {dispatch.expiry_at ? formatBangkokDateTime(dispatch.expiry_at) : '—'}</td>
                  <td>{typeof dispatch.lateness_minutes === 'number' ? `${dispatch.lateness_minutes} min` : '—'}</td>
                  <td><ScenarioStatus status={dispatch.status} /></td>
                  <td>
                    {dispatch.status === 'RELEASED' && (
                      <Button size="small" disabled={busyId !== null || !dispatch.planned_consumption_start_at} onClick={() => runStart(dispatch)}>
                        {busyId === dispatch.id ? 'Working…' : 'Start'}
                      </Button>
                    )}
                    {dispatch.status === 'IN_TRANSIT' && (
                      <Button size="small" variant="contained" disabled={busyId !== null} onClick={() => { setDeliverTarget(dispatch); }}>
                        Deliver
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Box>
        </Box>
      )}

      <ConfirmationDialog
        open={Boolean(deliverTarget)}
        title={`Confirm delivery of barrel ${deliverTarget?.core_tumbler ?? ''}`}
        description={(
          <Stack spacing={1.5}>
            <Typography>Dispatch #{deliverTarget?.id} to {deliverTarget?.downstream_line}. This cannot be undone from here.</Typography>
            <Typography>Full physical barrel: {deliverTarget?.quantity_kg} kg. Delivery removes it from cold-room stock; it does not confirm that line consumption has finished.</Typography>
          </Stack>
        )}
        confirmLabel="Deliver"
        busy={busyId === deliverTarget?.id}
        onCancel={() => setDeliverTarget(null)}
        onConfirm={confirmDeliver}
      />
    </Box>
  );
}
